import React, { MouseEvent, useCallback } from 'react';

import { useSnackbar } from 'notistack';

import { Button, ButtonProps, Link, LinkProps } from '@mui/material';

import getErrorMessage from '~/utils/getErrorMessage';
import { getVendorPortalUrl } from '~/utils/vendorPortal';

type VendorPortalNavigationProps = {
  portalPath: string;
  target?: string;
};

function useVendorPortalClick(
  portalPath: string,
  onClick?: (event: MouseEvent<HTMLElement>) => void,
  target = '_blank',
) {
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      onClick?.(event);

      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();

      try {
        const url = await getVendorPortalUrl(portalPath);
        window.open(url, target, 'noopener,noreferrer');
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'Failed to open Vendor Portal.'), { variant: 'error' });
      }
    },
    [enqueueSnackbar, onClick, portalPath, target],
  );
}

export function VendorPortalButton({
  portalPath,
  onClick,
  target = '_blank',
  ...props
}: VendorPortalNavigationProps & ButtonProps) {
  const handleClick = useVendorPortalClick(portalPath, onClick, target);

  return <Button {...props} onClick={handleClick} />;
}

export function VendorPortalLink({
  portalPath,
  onClick,
  target = '_blank',
  ...props
}: VendorPortalNavigationProps & LinkProps) {
  const handleClick = useVendorPortalClick(portalPath, onClick, target);

  return <Link {...props} href="javascript:void(0)" target={target} onClick={handleClick} />;
}
